import { toValue, type MaybeRefOrGetter } from 'vue'
import { useHead } from '#imports'
import type { Product } from '@scayle/storefront-nuxt'
import { useI18n } from 'vue-i18n'
import { sanitizeCanonical } from '~/utils/seo'
import { getProductDetailRoute } from '~/utils/route'
import { normalizeSize } from '../types/variant'
import useRouteHelpers from '../composables/useRouteHelpers'
import {
  hasSuppressingQuery,
  pickXDefaultHref,
  stripHreflangTrailingSlash,
} from '../utils/hreflang'

/**
 * Sets PDP head link tags: canonical, hreflang alternates, self reference, and x-default.
 * Canonical includes ?size= when the URL has a size variant. Shared by clothing-pdp (via usePdpSeo).
 */
export default function usePdpHeadLinks(
  product: MaybeRefOrGetter<Product | null | undefined>,
  productAlternateNames: MaybeRefOrGetter<
    Record<string, string> | null | undefined
  >,
) {
  const config = useRuntimeConfig()
  const i18n = useI18n()
  const route = useRoute()
  const { toLocalePath } = useRouteHelpers()

  useHead(() => {
    const p = toValue(product)
    if (!p) {
      return {}
    }

    const alternates = toValue(productAlternateNames)
    const currentIso = i18n.localeProperties.value.iso as string
    const canonicalBaseUrl = sanitizeCanonical(
      `${config.public.baseUrl}${toLocalePath(getProductDetailRoute(p))}`,
    )
    const hasSizeVariantUrl =
      typeof route.query.size === 'string' && route.query.size.length > 0
    const canonicalHref = hasSizeVariantUrl
      ? `${canonicalBaseUrl}?size=${encodeURIComponent(normalizeSize(route.query.size as string))}`
      : canonicalBaseUrl

    const canonicalTag = {
      rel: 'canonical',
      key: 'canonical',
      href: canonicalHref,
    }

    // Suppress hreflang entirely when the URL carries any query key other than
    // `page` (e.g. ?size=): the sized PDP is noindex, so a partial hreflang
    // group would be inconsistent (ECOM-2433). Canonical is unchanged.
    if (hasSuppressingQuery(route.query)) {
      return { link: [canonicalTag] }
    }

    const alternateLinks = Object.keys(alternates ?? {})
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({
        rel: 'alternate' as const,
        hreflang: key,
        href: stripHreflangTrailingSlash(alternates?.[key] ?? ''),
      }))

    const selfReference = {
      rel: 'alternate' as const,
      hreflang: currentIso,
      href: stripHreflangTrailingSlash(canonicalHref),
    }

    // x-default = the de-DE member verbatim (the self-reference carries it when
    // the current page is German); omit when no German member exists.
    const members = [selfReference, ...alternateLinks]
    const xDefaultHref = pickXDefaultHref(members)
    const xDefault = xDefaultHref
      ? { rel: 'alternate' as const, hreflang: 'x-default', href: xDefaultHref }
      : undefined

    return {
      link: [...members, ...(xDefault ? [xDefault] : []), canonicalTag],
    }
  })
}
